import { PenIcon, TrashAltIcon } from "assets/icons";
import { DragDropChangeValue, DragDropList } from "components";
import { useTranslation } from "react-i18next";
import { IconButton } from "ui";

import {
  CHANNEL_COLORS,
  CHANNEL_LABELS,
  WHATSAPP_TEMPLATE_MESSAGE_COST_FCFA
} from "../../constants";
import { EMarketingChannel, MarketingFlowStep } from "../../marketing.types";
import { StatusBadge } from "../StatusBadge";

import styles from "./FlowStepsList.module.scss";

interface FlowStepsListProps {
  steps: MarketingFlowStep[];
  onReorder: (stepIds: string[]) => void;
  onEdit: (step: MarketingFlowStep) => void;
  onDelete: (step: MarketingFlowStep) => void;
}

const FlowStepsList = ({
  steps,
  onReorder,
  onEdit,
  onDelete
}: FlowStepsListProps) => {
  const { t } = useTranslation();

  const cumulativeDays = (index: number): number =>
    steps
      .slice(0, index + 1)
      .reduce((total, step) => total + step.delayDays, 0);

  const handleDragChange = ({ fromIndex, toIndex }: DragDropChangeValue) => {
    const reordered = [...steps];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onReorder(reordered.map((step) => step.id));
  };

  return (
    <DragDropList
      data={steps}
      droppableId="marketing-flow-steps"
      onChange={handleDragChange}
      emptyText={t("No steps yet — add the first one")}
      renderItem={(step) => {
        const index = steps.findIndex((item) => item.id === step.id);

        return (
          <div key={step.id} className={styles.step}>
            <div className={styles.step__marker}>
              <span className={styles.step__day}>
                J+{cumulativeDays(index)}
              </span>
              <span className={styles.step__line} />
            </div>

            <div className={styles.step__card}>
              <div className={styles.step__content}>
                <span className={styles.step__message}>
                  {step.messageText ||
                    (step.templateName
                      ? `${t("Template")}: ${step.templateName}`
                      : t("No message"))}
                </span>

                <div className={styles.step__meta}>
                  {step.channel ? (
                    <StatusBadge
                      label={CHANNEL_LABELS[step.channel]}
                      color={CHANNEL_COLORS[step.channel]}
                    />
                  ) : (
                    <StatusBadge label={t("Auto channel")} color="#8E8E93" />
                  )}

                  {step.channel === EMarketingChannel.WhatsApp &&
                    (step.templateName ? (
                      <span className={styles.step__cost}>
                        ~{WHATSAPP_TEMPLATE_MESSAGE_COST_FCFA} F / {t("msg")}
                      </span>
                    ) : (
                      <span className={styles.step__cost}>
                        {t("Free inside the 24h window")}
                      </span>
                    ))}

                  {step.templateName && step.messageText && (
                    <span className={styles.step__cost}>
                      {t("Template")}: {step.templateName}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.step__actions}>
                <IconButton
                  icon={<PenIcon width={18} height={18} />}
                  onClick={() => onEdit(step)}
                />
                <IconButton
                  icon={<TrashAltIcon fill="#ff0033" width={18} height={18} />}
                  onClick={() => onDelete(step)}
                />
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

export default FlowStepsList;
